/**
 * AUREXIS COMMAND SIDEBAR
 * Collapsible: narrow icon rail by default (9 daily-use surfaces + a "More"
 * flyout for everything secondary), or expanded to show full labels and the
 * secondary groups — Control, Labs, Market, Ops — inline instead of hidden.
 * State persists across reloads (aurexis_rail_expanded).
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Brain,
  Microscope,
  Activity,
  Power,
  Target,
  Square,
  Loader2,
  FileCheck,
  FileSignature,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Settings,
  SlidersHorizontal,
  Network,
  Crosshair,
} from 'lucide-react';
import logo from '@/assets/core-brain-symbol-320.jpg';   // 320 px copy (46 kB) of the 954 kB original
import { useServiceHealth } from '@/contexts/ServiceHealthContext';
import { preloadAll, startBackgroundRefresh, stopBackgroundRefresh } from '@/services/candleCache';
import { chartSymbol } from '@/utils/chartSymbol';
import { useAuth } from '@/hooks/useAuth';

const AUTH_BASE = '/svc/auth';

/** Light preload — full 13-TF burst saturates candle-constructor on a 4-core box. */
const ENGINE_PRELOAD_TFS = ['5m', '15m', '1h', '4h', '1D'];

/* ── Primary rail ──
 * Exported so CommandHeader's search palette can index the same real
 * destinations instead of maintaining a second, driftable list. */
export const PRIMARY_TABS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/',
    exact: true,
  },
  {
    id: 'chart',
    label: 'Chart',
    icon: BarChart3,
    path: '/chart',
  },
  {
    id: 'learning',
    label: 'Learning',
    icon: Brain,
    path: '/learning',
  },
  {
    id: 'research',
    label: 'Research',
    icon: Microscope,
    path: '/research',
  },
  {
    id: 'setups',
    label: 'Setups',
    icon: Crosshair,
    path: '/setups',
  },
  {
    id: 'journal',
    label: 'Journal',
    icon: FileCheck,
    path: '/journal',
  },
  {
    id: 'weights',
    label: 'Weights',
    icon: SlidersHorizontal,
    path: '/weights',
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    icon: Network,
    path: '/knowledge',
  },
  {
    id: 'monitor',
    label: 'Monitor',
    icon: Activity,
    path: '/monitor',
  },
  {
    id: 'simulation',
    label: 'Simulation',
    icon: FlaskConical,
    path: '/simulation',
  },
  {
    id: 'features',
    label: 'Features',
    icon: Target,
    path: '/features',
  },
  {
    id: 'contracts',
    label: 'Contracts',
    icon: FileSignature,
    path: '/contracts',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings',
  },
];

// The "More" menu and its sixteen secondary pages were removed on 2026-10-12 (see App.tsx).
// The export stays, empty, so the header search keeps one list to read.
export const MORE_LINKS: Array<{ label: string; path: string; group: string }> = [];

type SystemMode = 'idle' | 'loading' | 'live';

interface CommandSidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  systemMode?: 'idle' | 'training' | 'autonomous';
}

export default function CommandSidebar({ systemMode = 'idle' }: CommandSidebarProps) {
  const location = useLocation();
  const { servicesOnline: onlineCount, total: totalCount } = useServiceHealth();
  const { user, accessToken } = useAuth();
  const [engineMode, setEngineMode] = useState<SystemMode>('idle');
  const [expanded, setExpanded] = useState(() => localStorage.getItem('aurexis_rail_expanded') === '1');

  useEffect(() => {
    document.documentElement.style.setProperty('--rail-width', expanded ? 'var(--sidebar-width)' : 'var(--sidebar-collapsed)');
    localStorage.setItem('aurexis_rail_expanded', expanded ? '1' : '0');
  }, [expanded]);

  // ── Kill-switch confirmation: stopping market-ingestion's live stream cuts
  // off the data every downstream decision is made from — the operator called
  // this "the kill switch that stops the AI from making the decision" and
  // asked it be gated behind email + a verification code. There's no
  // send-an-email-code endpoint anywhere in this codebase (checked), and
  // guessing one for a safety control is worse than not having the gate at
  // all — a 404 on "send code" would leave the operator unable to ever stop
  // the engine. This reuses the one real, already-proven verification path
  // (the same /api/two-factor/verify TOTP check Settings.tsx uses) instead.
  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    }),
    [accessToken],
  );
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${AUTH_BASE}/api/auth/me`, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          const u = data.user || data.data || data;
          setTwoFactorEnabled(u.twoFactorEnabled === true);
        }
      } catch { /* auth service unreachable — leave the fetched default */ }
    })();
  }, [authHeaders]);

  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);

  const closeStopConfirm = () => {
    setShowStopConfirm(false);
    setConfirmEmail(''); setConfirmCode(''); setConfirmPhrase(''); setConfirmError('');
  };

  const confirmStopEngine = async () => {
    const realEmail = (user?.email || '').trim().toLowerCase();
    if (!realEmail || confirmEmail.trim().toLowerCase() !== realEmail) {
      setConfirmError('Email does not match the signed-in account.');
      return;
    }
    setConfirmBusy(true);
    setConfirmError('');
    if (twoFactorEnabled) {
      if (!confirmCode || confirmCode.length < 6) {
        setConfirmError('Enter the 6-digit code from your authenticator app.');
        setConfirmBusy(false);
        return;
      }
      try {
        const res = await fetch(`${AUTH_BASE}/api/two-factor/verify`, {
          method: 'POST', headers: authHeaders, body: JSON.stringify({ token: confirmCode }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setConfirmError(data.message || 'Invalid verification code.');
          setConfirmBusy(false);
          return;
        }
      } catch {
        setConfirmError('Could not reach auth service to verify the code.');
        setConfirmBusy(false);
        return;
      }
    } else if (confirmPhrase.trim().toUpperCase() !== 'STOP ENGINE') {
      setConfirmError('2FA is not enabled on this account — type STOP ENGINE exactly to confirm.');
      setConfirmBusy(false);
      return;
    }
    setConfirmBusy(false);
    closeStopConfirm();
    await handleStopEngine();
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleStartEngine = async () => {
    if (engineMode === 'loading') return;
    setEngineMode('loading');
    const sym = chartSymbol();
    // Only the timeframes the chart actually uses — NOT all 13 (1s/2s/1m…),
    // which was drowning candle-constructor and making the chart go blank.
    try {
      await preloadAll(sym, ENGINE_PRELOAD_TFS);
      startBackgroundRefresh(sym);
    } catch (e) {
      console.warn('[AUREXIS] Candle preload failed:', e);
    }
    try {
      const res = await fetch('/svc/market-ingestion/stream/start', { method: 'POST' });
      const data = await res.json();
      console.info('[AUREXIS] Deriv stream:', data.status, data.message ?? '');
    } catch (e) {
      console.warn('[AUREXIS] Could not start Deriv stream:', e);
    }
    setEngineMode('live');
  };

  // The button only ever started the pipeline — there was no way to stop it short
  // of killing the tab. market-ingestion has a real /stream/stop; wire it in.
  const handleStopEngine = async () => {
    if (engineMode !== 'live') return;
    setEngineMode('loading');
    stopBackgroundRefresh();
    try {
      const res = await fetch('/svc/market-ingestion/stream/stop', { method: 'POST' });
      const data = await res.json();
      console.info('[AUREXIS] Deriv stream:', data.status, data.message ?? '');
    } catch (e) {
      console.warn('[AUREXIS] Could not stop Deriv stream:', e);
    }
    setEngineMode('idle');
  };

  const handleEngineClick = () => {
    // Stopping the engine is the kill switch — gate it, never fire it directly.
    if (engineMode === 'live') { setShowStopConfirm(true); return; }
    if (engineMode === 'idle') { handleStartEngine(); }
  };

  // ── Light boot on page load: current chart TF only (no 13-TF storm) ──
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return;
    autoStarted.current = true;
    const sym = chartSymbol();
    const tf = localStorage.getItem('aurexis_timeframe') || '1h';
    const bootTfs = [tf, '5m'].filter((v, i, a) => a.indexOf(v) === i);
    const t = setTimeout(async () => {
      setEngineMode('loading');
      try {
        await preloadAll(sym, bootTfs);
        startBackgroundRefresh(sym);
        setEngineMode('live');
      } catch {
        setEngineMode('idle');
      }
    }, 1200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const engineColor =
    engineMode === 'live'    ? '#10b981' :
    engineMode === 'loading' ? '#f59e0b' :
    '#64748b';

  const engineLabel =
    engineMode === 'live'    ? 'STOP' :
    engineMode === 'loading' ? 'LOADING' :
    'START';

  return (
    <>
    <aside className={`aurexis-sidebar-rail${expanded ? ' expanded' : ''}`}>
      <button
        type="button"
        className="rail-collapse-btn"
        title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
      </button>

      {/* Logo */}
      <div className="rail-logo">
        <img src={logo} alt="AUREXIS" className="rail-logo-img" />
        {expanded && <span className="rail-logo-title">AUREXIS</span>}
      </div>

      {/* Primary nav tabs */}
      <nav className="rail-nav">
        {PRIMARY_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path, tab.exact);
          return (
            <NavLink
              key={tab.id}
              to={tab.path}
              className={`rail-tab${active ? ' active' : ''}`}
              title={tab.label}
            >
              <Icon size={20} />
              <span className="rail-tab-label">{tab.label}</span>
            </NavLink>
          );
        })}

      </nav>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* START ENGINE button */}
      <button
        className={`rail-tab rail-engine-btn${engineMode === 'live' ? ' active' : ''}`}
        style={{ color: engineColor, cursor: engineMode === 'loading' ? 'wait' : 'pointer', border: 'none', background: 'none', width: '100%' }}
        title={engineMode === 'idle' ? 'Start Engine — load all candle data' : engineMode === 'loading' ? 'Working…' : 'Engine live — click to stop'}
        onClick={handleEngineClick}
      >
        {engineMode === 'loading'
          ? <Loader2 size={16} className="rail-spin" />
          : engineMode === 'live'
          ? <Square size={16} />
          : <Power size={16} />
        }
        <span className="rail-tab-label" style={{ fontSize: '9px', letterSpacing: '0.06em', fontWeight: 700 }}>
          {engineLabel}
        </span>
      </button>

      {/* Online count */}
      <div className="rail-status-badge" title={`${onlineCount} / ${totalCount} services online`}>
        <span className={`rail-dot ${onlineCount > 0 ? 'online' : 'offline'}`} />
        <span className="rail-tab-label" style={{ fontSize: '9px' }}>
          {onlineCount}/{totalCount}
        </span>
      </div>

    </aside>

    {showStopConfirm && (
      <div
        onClick={closeStopConfirm}
        style={{
          position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(3,6,12,0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 380, background: '#0a0e16', border: '1px solid rgba(239,68,68,0.4)',
            borderRadius: 10, padding: '18px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <ShieldAlert size={18} color="#ef4444" />
            <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em' }}>
              STOP ENGINE — CONFIRM IDENTITY
            </span>
          </div>
          <p style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.5, margin: '8px 0 14px' }}>
            This halts the live market-data stream every downstream decision is made from.
            Confirm it's really you before the AI stops.
          </p>

          <label style={{ display: 'block', fontSize: 10, color: '#64748b', marginBottom: 4 }}>
            Account email
          </label>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={user?.email || 'your@email'}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 9px', marginBottom: 10,
              background: '#050810', border: '1px solid #1e293b', borderRadius: 5,
              color: '#e2e8f0', fontSize: 12, fontFamily: 'inherit',
            }}
          />

          {twoFactorEnabled ? (
            <>
              <label style={{ display: 'block', fontSize: 10, color: '#64748b', marginBottom: 4 }}>
                6-digit authenticator code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '7px 9px', marginBottom: 10,
                  background: '#050810', border: '1px solid #1e293b', borderRadius: 5,
                  color: '#e2e8f0', fontSize: 14, letterSpacing: '0.3em', fontFamily: 'inherit',
                }}
              />
            </>
          ) : (
            <>
              <label style={{ display: 'block', fontSize: 10, color: '#64748b', marginBottom: 4 }}>
                2FA isn't enabled on this account — type STOP ENGINE to confirm
              </label>
              <input
                type="text"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="STOP ENGINE"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '7px 9px', marginBottom: 10,
                  background: '#050810', border: '1px solid #1e293b', borderRadius: 5,
                  color: '#e2e8f0', fontSize: 12, fontFamily: 'inherit',
                }}
              />
            </>
          )}

          {confirmError && (
            <div style={{ color: '#f87171', fontSize: 10.5, marginBottom: 10 }}>{confirmError}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={closeStopConfirm}
              disabled={confirmBusy}
              style={{
                flex: 1, padding: '8px 0', background: 'transparent', border: '1px solid #1e293b',
                borderRadius: 6, color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={confirmStopEngine}
              disabled={confirmBusy}
              style={{
                flex: 1, padding: '8px 0', background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444',
                borderRadius: 6, color: '#ef4444', fontSize: 11, fontWeight: 700, cursor: confirmBusy ? 'wait' : 'pointer',
                fontFamily: 'inherit',
              }}
            >{confirmBusy ? 'VERIFYING…' : 'CONFIRM STOP'}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export { CommandSidebar };
